import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, RefreshCw, Send, SendHorizonal } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { sendAdminDispatch } from "@/lib/api";

const fmt = (n) => Number(n ?? 0).toLocaleString();

const stamp = (iso) => {
  if (!iso) return "Never sent";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const DispatchPanel = ({ token, data, loading, onRefresh }) => {
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmFor, setConfirmFor] = useState(null);

  if (loading && !data) {
    return (
      <div className="mt-6 grid gap-3" data-testid="dispatch-loading">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="panel h-32 animate-pulse bg-[hsl(220_18%_8%)] p-4"
          />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <p
        data-testid="dispatch-error"
        className="panel mt-6 px-4 py-10 text-center text-sm text-muted-foreground"
      >
        Dispatch registry unavailable.{" "}
        <button
          type="button"
          onClick={onRefresh}
          className="font-mono text-[12px] uppercase tracking-[0.14em] text-[hsl(174_62%_64%)] hover:underline"
        >
          Retry
        </button>
      </p>
    );
  }

  const dispatches = data.dispatches || [];

  const runTest = async (campaign) => {
    const address = testTo.trim();
    if (!address) {
      toast.error("Enter a rehearsal address first");
      return;
    }
    setBusy(`test:${campaign}`);
    try {
      await sendAdminDispatch(token, { campaign, testTo: address });
      toast.success(`Rehearsal dispatch sent to ${address}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Test send failed");
    } finally {
      setBusy("");
    }
  };

  const runBroadcast = async (campaign) => {
    setBusy(`send:${campaign}`);
    try {
      const res = await sendAdminDispatch(token, { campaign, confirm: true });
      toast.success(
        `Dispatched to ${fmt(res.sent)} · ${fmt(res.skipped)} already had it` +
          (res.failed ? ` · ${fmt(res.failed)} failed` : "")
      );
      onRefresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Dispatch failed");
    } finally {
      setBusy("");
      setConfirmFor(null);
    }
  };

  return (
    <div className="mt-6 space-y-4" data-testid="dispatch-panel">
      <div className="panel p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Signal registry</p>
            <p
              data-testid="dispatch-registry-total"
              className="mt-1 font-mono text-3xl tabular-nums text-foreground"
            >
              {fmt(data.registry_total)}
            </p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
              {data.email_enabled
                ? "Transport live · release-day sends only"
                : "Transport offline · configure email first"}
            </p>
          </div>
          <button
            type="button"
            onClick={onRefresh}
            data-testid="dispatch-refresh-button"
            className="inline-flex h-10 items-center gap-2 rounded-[10px] border border-border px-3 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-150 hover:bg-[hsl(220_18%_12%)] hover:text-foreground"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>

        <div className="mt-4 border-t border-border pt-4">
          <label
            htmlFor="dispatch-test-address"
            className="eyebrow block"
          >
            Rehearsal address
          </label>
          <Input
            id="dispatch-test-address"
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            data-testid="dispatch-test-input"
            className="mt-2 max-w-sm font-mono text-[13px]"
          />
          <p className="mt-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.14em] text-[hsl(215_8%_45%)]">
            Send yourself the exact dispatch first. Rehearsals are never logged
            against the registry.
          </p>
        </div>
      </div>

      {dispatches.map((d) => {
        const sending = busy === `send:${d.campaign}`;
        const testing = busy === `test:${d.campaign}`;
        return (
          <div
            key={d.campaign}
            className="panel p-4"
            data-testid={`dispatch-card-${d.campaign}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="eyebrow">Release dispatch</p>
                <h3 className="mt-1 text-lg font-semibold text-foreground">
                  {d.label}
                </h3>
                <p className="mt-1 flex items-center gap-2 font-mono text-[11px] text-muted-foreground">
                  <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                  {d.subject}
                </p>
              </div>
              <span className="rounded-[6px] border border-[hsl(174_62%_40%/0.5)] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(174_62%_64%)]">
                {stamp(d.last_sent_at)}
              </span>
            </div>

            <dl className="mt-4 grid grid-cols-3 gap-3 border-t border-border pt-4">
              {[
                ["Pending", d.pending, `dispatch-pending-${d.campaign}`],
                ["Sent", d.sent, `dispatch-sent-${d.campaign}`],
                ["Failed", d.failed, `dispatch-failed-${d.campaign}`]
              ].map(([label, value, testId]) => (
                <div key={label}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
                    {label}
                  </dt>
                  <dd
                    data-testid={testId}
                    className="mt-1 font-mono text-xl tabular-nums text-foreground"
                  >
                    {fmt(value)}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => runTest(d.campaign)}
                disabled={Boolean(busy) || !data.email_enabled}
                data-testid={`dispatch-test-button-${d.campaign}`}
                className="inline-flex h-11 items-center gap-2 rounded-[10px] border border-border px-4 font-mono text-[11px] uppercase tracking-[0.14em] text-foreground transition-colors duration-150 hover:bg-[hsl(220_18%_12%)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {testing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                Rehearsal send
              </button>
              <button
                type="button"
                onClick={() => setConfirmFor(d)}
                disabled={
                  Boolean(busy) || !data.email_enabled || d.pending === 0
                }
                data-testid={`dispatch-send-button-${d.campaign}`}
                className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-primary px-4 font-mono text-[11px] uppercase tracking-[0.14em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <SendHorizonal className="h-3.5 w-3.5" />
                )}
                Dispatch to registry
              </button>
            </div>
            {d.pending === 0 && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(215_8%_45%)]">
                No addresses waiting — everyone on file already has this one.
              </p>
            )}
          </div>
        );
      })}

      <AlertDialog
        open={Boolean(confirmFor)}
        onOpenChange={(open) => !open && setConfirmFor(null)}
      >
        <AlertDialogContent data-testid="dispatch-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Break the seal?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends “{confirmFor?.label}” to{" "}
              {fmt(confirmFor?.pending)} registered address
              {confirmFor?.pending === 1 ? "" : "es"}. Anyone who already
              received it is skipped. This cannot be unsent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dispatch-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="dispatch-confirm-send"
              onClick={() => runBroadcast(confirmFor.campaign)}
            >
              Send dispatch
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
